import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import {
  axiosInstance,
  useClientInfo,
  useAppContext,
} from "../utils/commonImports";
import { getFYRange, resolveDateRange } from "../utils/fy";

const REPORTS_PATH = "/reports"; // change if your dashboard route differs

/* Amount columns shown before the GST columns */
const AMOUNT_COLS = [
  { key: "basic_amount", label: "Basic", width: 75 },
  { key: "escort_amount", label: "Escort", width: 70 },
  { key: "batta_amount", label: "Batta", width: 70 },
  { key: "parking_amount", label: "Parking", width: 70 },
  { key: "others_amount", label: "Others", width: 70 },
  { key: "permit_amount", label: "Permit", width: 70 },
  { key: "toll_amount", label: "Toll", width: 70 },
  { key: "fuel_esc_amount", label: "Fuel", width: 70 },
  { key: "addition_amount", label: "Addition", width: 70 },
  { key: "discount", label: "Discount", width: 70 },
];

const RCM_TREATMENT = "Registered Business-Under RCM";

const num = (v) => Number(v || 0).toFixed(2);

const datePickerSx = {
  width: 160,
  "& .MuiInputBase-root": {
    height: 34,
    fontSize: "0.75rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
  },
};

export default function SalesRegister() {
  const navigate = useNavigate();
  const { clientId } = useClientInfo();

  // Financial year selected in the header
  const { selectedFinancialYear, financialYearLoading } = useAppContext();
  const fy = selectedFinancialYear?.label || ""; // e.g. "2025-26"
  const { start: fyStart, end: fyEnd } = getFYRange(selectedFinancialYear);

  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customer, setCustomer] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [searchVersion, setSearchVersion] = useState(0);

  /* ================= BACK BUTTON -> Reports dashboard ================= */
  const handleBack = () => navigate(REPORTS_PATH);

  /* ================= LOAD CUSTOMERS ================= */
  useEffect(() => {
    if (!clientId) return;

    axiosInstance
      .get("/api/customers", {
        params: { client_id: clientId },
      })
      .then((res) => setCustomers(res.data || []))
      .catch(console.error);
  }, [clientId]);

  const handleSearch = () => setSearchVersion((v) => v + 1);

  const handlePrint = () => {
    window.print();
  };

  // Opens the browser print dialog; choose "Save as PDF" as the destination.
  const handlePdf = () => {
    window.print();
  };

  const handleExcel = () => {
    const headers = [
      "Invoice Number",
      "Invoice Date",
      "Customer",
      ...AMOUNT_COLS.map((c) => c.label),
      "IGST %",
      "CGST %",
      "SGST %",
      "IGST Amount",
      "CGST Amount",
      "SGST Amount",
      "RCM IGST",
      "RCM CGST",
      "RCM SGST",
      "Net Amount",
    ];

    const escapeCsv = (value) => {
      const text = value == null ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const csvRows = rows.map((r) => [
      r.invoice_number,
      r.invoice_date ? dayjs(r.invoice_date).format("DD/MM/YYYY") : "",
      r.customername,
      ...AMOUNT_COLS.map((c) => r[c.key]),
      r.igst_percent,
      r.cgst_percent,
      r.sgst_percent,
      r.igst_amount,
      r.cgst_amount,
      r.sgst_amount,
      isRCMRow(r) ? r.igst_amount : 0,
      isRCMRow(r) ? r.cgst_amount : 0,
      isRCMRow(r) ? r.sgst_amount : 0,
      r.net_amount,
    ]);

    const csv = "\\uFEFF" + [
      headers.map(escapeCsv).join(","),
      ...csvRows.map((row) => row.map(escapeCsv).join(",")),
    ].join("\\r\\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Sales_Register_${fy || "Report"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  /* ================= FY CHANGED: clear manual dates ================= */
  useEffect(() => {
    setFromDate(null);
    setToDate(null);
  }, [selectedFinancialYear?.fyid]);

  /* ================= LOAD DATA (FY aware) ================= */
  useEffect(() => {
    if (!clientId || financialYearLoading) return;

    let cancelled = false;

    // picked dates win, but are clamped inside the selected FY
    const { from, to } = resolveDateRange(
      selectedFinancialYear,
      fromDate,
      toDate,
    );

    setLoading(true);
    setReportError("");

    axiosInstance
      .get("/api/invoices/sales-register", {
        params: {
          client_id: clientId,
          fromDate: from || null,
          toDate: to || null,
          fyid: selectedFinancialYear?.fyid || "",
          fy_id: selectedFinancialYear?.fyid || "",
          fy,
          customer_id: customer === "ALL" ? null : customer,
        },
      })
      .then((res) => {
        if (!cancelled) {
          const data = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
              ? res.data.data
              : [];
          setRows(data);
        }
      })
      .catch((err) => {
        console.error("Sales Register load error:", err.response?.data || err);
        if (!cancelled) {
          setRows([]);
          setReportError(
            err.response?.data?.message ||
            err.message ||
            "Failed to load Sales Register."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // ignore a slow response from an older request
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    fromDate,
    toDate,
    customer,
    selectedFinancialYear?.fyid,
    financialYearLoading,
    searchVersion,
  ]);

  /* ================= TOTALS ================= */
  const sumOf = (list, field) =>
    list.reduce((t, r) => t + Number(r[field] || 0), 0);

  const isRCMRow = (r) => r.gst_treatment === RCM_TREATMENT;
  const normalRows = rows.filter((r) => !isRCMRow(r));
  const rcmRows = rows.filter((r) => isRCMRow(r));

  const totals = {
    igst: sumOf(normalRows, "igst_amount"),
    cgst: sumOf(normalRows, "cgst_amount"),
    sgst: sumOf(normalRows, "sgst_amount"),
    rcm_igst: sumOf(rcmRows, "igst_amount"),
    rcm_cgst: sumOf(rcmRows, "cgst_amount"),
    rcm_sgst: sumOf(rcmRows, "sgst_amount"),
    net: sumOf(rows, "net_amount"),
  };

  const groupedData =
    customer === "ALL"
      ? rows.reduce((acc, row) => {
          const key = row.customername || "Unknown";
          if (!acc[key]) acc[key] = [];
          acc[key].push(row);
          return acc;
        }, {})
      : null;

  /* ================= INVOICE ROW ================= */
  const renderInvoiceRow = (r, i) => {
    const isRCM = r.gst_treatment?.toLowerCase().includes("rcm");

    return (
      <TableRow
        key={i}
        sx={{
          background: i % 2 === 0 ? "#fff" : "#fafafa",
        }}
      >
        <TableCell>{r.invoice_number}</TableCell>
        <TableCell>{dayjs(r.invoice_date).format("DD/MM/YYYY")}</TableCell>
        <TableCell>{r.customername}</TableCell>

        {AMOUNT_COLS.map((c) => (
          <TableCell key={c.key} align="right">
            {num(r[c.key])}
          </TableCell>
        ))}

        {/* GST % */}
        <TableCell align="right">{num(r.igst_percent)}</TableCell>
        <TableCell align="right">{num(r.cgst_percent)}</TableCell>
        <TableCell align="right">{num(r.sgst_percent)}</TableCell>

        {/* GST Amount */}
        <TableCell align="right">{isRCM ? "0.00" : num(r.igst_amount)}</TableCell>
        <TableCell align="right">{isRCM ? "0.00" : num(r.cgst_amount)}</TableCell>
        <TableCell align="right">{isRCM ? "0.00" : num(r.sgst_amount)}</TableCell>

        {/* RCM GST */}
        <TableCell align="right">{isRCM ? num(r.igst_amount) : "0.00"}</TableCell>
        <TableCell align="right">{isRCM ? num(r.cgst_amount) : "0.00"}</TableCell>
        <TableCell align="right">{isRCM ? num(r.sgst_amount) : "0.00"}</TableCell>

        <TableCell align="right" sx={{ fontWeight: 700 }}>
          {num(r.net_amount)}
        </TableCell>
      </TableRow>
    );
  };

  /* ================= UI ================= */
  const headerCellSx = {
    "& th": {
      backgroundColor: "#f4f4f4",
      fontWeight: 700,
      fontSize: "0.70rem",
      border: "1px solid #9e9e9e",
      textAlign: "center",
      padding: "6px 8px",
      whiteSpace: "nowrap",
    },
  };

  return (
    <Box
      sx={{
        mb: 1,
        "@media print": {
          "& .no-print": { display: "none !important" },
          "& .MuiPaper-root": { boxShadow: "none !important" },
          "& .MuiTableContainer-root": { maxHeight: "none !important", overflow: "visible !important" },
          "& table": { minWidth: "0 !important" },
          "& th, & td": { fontSize: "8px !important", padding: "3px !important" },
        },
      }}
    >
      {/* HEADER WITH BACK BUTTON */}
      <Box
        className="sales-report-header no-print"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        mb={2}
        flexWrap="wrap"
      >
        <Tooltip title="Back">
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "&:hover": { background: "#e8f1ff", color: "#1a73e8" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component="button"
              type="button"
              underline="hover"
              color="text.secondary"
              variant="body2"
              onClick={() => navigate(REPORTS_PATH)}
            >
              Reports
            </Link>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              Sales Register
            </Typography>
          </Breadcrumbs>

          <Typography variant="h6" fontWeight={700}>
            Sales Register
            {fy && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                FY {fy}
              </Typography>
            )}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={loading || financialYearLoading || !clientId}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={loading || rows.length === 0}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePdf}
            disabled={loading || rows.length === 0}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExcel}
            disabled={loading || rows.length === 0}
            sx={{ minWidth: 105, height: 40, textTransform: "uppercase" }}
          >
            Excel
          </Button>
        </Box>
      </Box>

      {/* FILTER BAR */}
      <Box
        className="no-print"
        sx={{
          display: "grid",
          gridTemplateColumns: "160px 160px 260px 110px",
          gap: 2,
          alignItems: "end",
          background: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: "6px",
          p: 2,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            format="DD/MM/YYYY"
            value={fromDate ? dayjs(fromDate) : null}
            minDate={fyStart ? dayjs(fyStart) : undefined}
            maxDate={fyEnd ? dayjs(fyEnd) : undefined}
            onChange={(newValue) =>
              setFromDate(newValue ? newValue.format("YYYY-MM-DD") : "")
            }
            slotProps={{ textField: { size: "small", sx: datePickerSx } }}
          />

          <DatePicker
            label="To Date"
            format="DD/MM/YYYY"
            value={toDate ? dayjs(toDate) : null}
            minDate={fyStart ? dayjs(fyStart) : undefined}
            maxDate={fyEnd ? dayjs(fyEnd) : undefined}
            onChange={(newValue) =>
              setToDate(newValue ? newValue.format("YYYY-MM-DD") : "")
            }
            slotProps={{ textField: { size: "small", sx: datePickerSx } }}
          />
        </LocalizationProvider>

        <FormControl variant="outlined" size="small">
          <InputLabel>Customer</InputLabel>
          <Select
            value={customer}
            label="Customer"
            onChange={(e) => setCustomer(e.target.value)}
          >
            <MenuItem value="ALL">All Customers</MenuItem>
            {customers.map((c) => (
              <MenuItem key={c.customerid} value={c.customerid}>
                {c.customername}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={loading || financialYearLoading || !clientId}
          sx={{ height: 40, textTransform: "uppercase" }}
        >
          Search
        </Button>
      </Box>

      {reportError && (
        <Alert severity="error" sx={{ mt: 1, mb: 1 }} className="no-print">
          {reportError}
        </Alert>
      )}

      {loading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }} className="no-print">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading Sales Register...
          </Typography>
        </Box>
      )}

      {customer !== "ALL" && rows.length > 0 && (
        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            padding: "8px 12px",
            borderLeft: "5px solid #1976d2",
            marginBottom: "8px",
            borderRadius: "4px",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            Customer: {rows[0].customername}
          </Typography>
        </Box>
      )}

      {/* TABLE */}
      <Paper sx={{ mt: 1.5 }}>
        <TableContainer sx={{ maxHeight: "75vh", border: "1px solid #999" }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed",
              minWidth: 1700,
              "& th, & td": {
                border: "1px solid #ccc",
                fontSize: "0.70rem",
                padding: "4px 6px",
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              {/* ===== GROUP HEADER ROW ===== */}
              <TableRow sx={headerCellSx}>
                <TableCell rowSpan={2} sx={{ width: 90 }}>
                  Inv No
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 70 }}>
                  Date
                </TableCell>
                <TableCell rowSpan={2} sx={{ width: 200 }}>
                  Customer
                </TableCell>

                {AMOUNT_COLS.map((c) => (
                  <TableCell
                    key={c.key}
                    rowSpan={2}
                    sx={{ width: c.width }}
                    align="right"
                  >
                    {c.label}
                  </TableCell>
                ))}

                <TableCell colSpan={3} sx={{ width: 135 }}>
                  GST %
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  GST Amount
                </TableCell>
                <TableCell colSpan={3} sx={{ width: 210 }}>
                  RCM GST
                </TableCell>

                <TableCell rowSpan={2} sx={{ width: 90 }} align="right">
                  Net
                </TableCell>
              </TableRow>

              {/* ===== SUB HEADER ROW ===== */}
              <TableRow sx={headerCellSx}>
                {/* GST % */}
                <TableCell sx={{ width: 45 }} align="right">
                  IGST%
                </TableCell>
                <TableCell sx={{ width: 45 }} align="right">
                  CGST%
                </TableCell>
                <TableCell sx={{ width: 45 }} align="right">
                  SGST%
                </TableCell>

                {/* GST Amount */}
                <TableCell sx={{ width: 70 }} align="right">
                  IGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  CGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  SGST
                </TableCell>

                {/* RCM GST */}
                <TableCell sx={{ width: 70 }} align="right">
                  IGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  CGST
                </TableCell>
                <TableCell sx={{ width: 70 }} align="right">
                  SGST
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {customer === "ALL"
                ? Object.entries(groupedData).map(
                    ([custName, custRows], idx) => {
                      const sub = (field) => sumOf(custRows, field);

                      return (
                        <React.Fragment key={idx}>
                          {/* ===== CUSTOMER TITLE ROW ===== */}
                          <TableRow sx={{ backgroundColor: "#dfe6e9" }}>
                            <TableCell
                              colSpan={999}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.85rem",
                                padding: "8px",
                              }}
                            >
                              {custName}
                            </TableCell>
                          </TableRow>

                          {/* ===== INVOICE ROWS ===== */}
                          {custRows.map(renderInvoiceRow)}

                          {/* ===== SUBTOTAL ROW ===== */}
                          <TableRow
                            sx={{
                              backgroundColor: "#f1f2f6",
                              "& td": {
                                fontWeight: 700,
                                borderTop: "3px solid #000",
                                borderBottom: "2px solid #000",
                                borderLeft: "1px solid #9e9e9e",
                                borderRight: "1px solid #9e9e9e",
                              },
                            }}
                          >
                            <TableCell colSpan={3}>Subtotal</TableCell>

                            {AMOUNT_COLS.map((c) => (
                              <TableCell key={c.key} align="right">
                                {num(sub(c.key))}
                              </TableCell>
                            ))}

                            {/* GST % empty */}
                            <TableCell colSpan={3}></TableCell>

                            {/* GST Amount totals */}
                            <TableCell align="right">
                              {num(sub("igst_amount"))}
                            </TableCell>
                            <TableCell align="right">
                              {num(sub("cgst_amount"))}
                            </TableCell>
                            <TableCell align="right">
                              {num(sub("sgst_amount"))}
                            </TableCell>

                            {/* RCM empty */}
                            <TableCell colSpan={3}></TableCell>

                            <TableCell align="right">
                              {num(sub("net_amount"))}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    },
                  )
                : rows.map(renderInvoiceRow)}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={26} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow
                sx={{
                  backgroundColor: "#eeeeee",
                  borderTop: "4px double #333",
                  "& td": {
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    border: "1px solid #9e9e9e",
                    padding: "6px 8px",
                  },
                }}
              >
                <TableCell colSpan={3}>TOTAL</TableCell>

                {AMOUNT_COLS.map((c) => (
                  <TableCell key={c.key} align="right">
                    {num(sumOf(rows, c.key))}
                  </TableCell>
                ))}

                {/* GST % (empty) */}
                <TableCell />
                <TableCell />
                <TableCell />

                {/* GST Amount */}
                <TableCell align="right">{num(totals.igst)}</TableCell>
                <TableCell align="right">{num(totals.cgst)}</TableCell>
                <TableCell align="right">{num(totals.sgst)}</TableCell>

                {/* RCM GST Amount */}
                <TableCell align="right">{num(totals.rcm_igst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_cgst)}</TableCell>
                <TableCell align="right">{num(totals.rcm_sgst)}</TableCell>

                {/* Net */}
                <TableCell align="right">{num(totals.net)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
